// F-069 : suivi des sinistres (dossier d'assurance distinct de l'incident technique).
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { patch, post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, money } from "@core/format";
import { useOrg } from "@core/org";
import { Button, Card, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Table, Textarea, fieldError } from "@core/ui";
import { BackLink, CLAIM_EVENTS, CLAIM_KINDS, CLAIM_ROLES, CLAIM_STATUS, DocLinks, PropertySelect, Status, Timeline, options, orgApi, sendFile, todayIso, useAction } from "./shared";

export function ClaimsList({ orgId }: { orgId: string }) {
  const org = useOrg();
  const nav = useNavigate();
  const q = useGet<{ items: any[] }>(orgApi(orgId, "/claims"));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ property_id: "", kind: "degat_des_eaux", title: "", occurred_on: "", description: "" });
  const act = useAction();
  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted">Un sinistre suit la déclaration, l’expertise et l’indemnisation ; il peut rester ouvert après la réparation. Aucun assureur n’est connecté : le dossier se transmet par vos soins.</p>
        {org.can("operate") ? <Button onClick={() => setOpen(true)}>Nouveau sinistre</Button> : null}
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.data?.items.length ? (
        <Table caption="Sinistres">
          <thead>
            <tr>
              <th>Sinistre</th>
              <th>Logement</th>
              <th>Statut</th>
              <th className="right">Reçu</th>
              <th>Prochaine action</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/espace/${orgId}/sinistres/${c.id}`}>{c.title}</Link>
                  <span className="block small muted">{CLAIM_KINDS[c.kind]}</span>
                </td>
                <td>{c.property_label}</td>
                <td>
                  <Status value={c.status} labels={CLAIM_STATUS} />
                </td>
                <td className="vie-amount">{money(c.received_cents)}</td>
                <td className="small">{c.next_action_on ? `${date(c.next_action_on)} — ${c.next_action}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Empty title="Aucun sinistre" />
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Nouveau sinistre"
        footer={
          <Button
            busy={act.busy}
            onClick={async () => {
              const r = await act.run(() => post<{ id: string }>(orgApi(orgId, "/claims"), { ...form, occurred_on: form.occurred_on || undefined, property_id: form.property_id || undefined }), "Dossier ouvert.");
              if (r) nav(`/espace/${orgId}/sinistres/${r.id}`);
            }}
          >
            Ouvrir le dossier
          </Button>
        }
      >
        <div className="stack">
          <ErrorBox error={act.error} />
          <PropertySelect orgId={orgId} value={form.property_id} error={fieldError(act.error, "property_id")} onChange={(v) => setForm({ ...form, property_id: v })} />
          <Select label="Nature" value={form.kind} options={options(CLAIM_KINDS)} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
          <Input label="Titre" required value={form.title} error={fieldError(act.error, "title")} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input label="Date du sinistre" type="date" value={form.occurred_on} error={fieldError(act.error, "occurred_on")} onChange={(e) => setForm({ ...form, occurred_on: e.target.value })} />
          <Textarea label="Faits" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}

export function ClaimPage() {
  const org = useOrg();
  const { claimId = "" } = useParams();
  const path = orgApi(org.orgId, `/claims/${claimId}`);
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  const refresh = () => invalidate(path, orgApi(org.orgId, "/claims"));
  const [status, setStatus] = useState({ status: "", next_action_on: "", next_action: "", franchise_cents: null as number | null, expected_cents: null as number | null });
  const [contact, setContact] = useState({ role: "assureur_proprietaire", name: "", reference: "", contact: "" });
  const [event, setEvent] = useState({ kind: "declaration", event_on: todayIso(), amount_cents: null as number | null, body: "" });
  const act = useAction();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const { claim: c, contacts, events, documents, totals } = q.data;
  const canOp = org.can("operate");
  return (
    <div className="page">
      <BackLink to={org.to("incidents?onglet=sinistres")}>Sinistres</BackLink>
      <PageHeader eyebrow={c.property_label} title={c.title} intro={CLAIM_KINDS[c.kind]} actions={<Status value={c.status} labels={CLAIM_STATUS} />} />
      {q.data.incident_resolved_claim_open ? <Notice tone="info">L’incident technique est résolu ; le dossier d’assurance reste ouvert jusqu’à sa conclusion.</Notice> : null}
      <Notice tone="attention">{q.data.rappel}</Notice>
      <ErrorBox error={act.error} />
      <div className="grid-2">
        <Card title="Dossier">
          <KeyValues
            items={[
              ["Survenu le", date(c.occurred_on)],
              ["Déclaré le", date(c.declared_on)],
              ["Franchise", money(totals.franchise_cents)],
              ["Indemnité attendue", money(totals.expected_cents)],
              ["Reçu", money(totals.received_cents)],
              ["Reste attendu", money(totals.remaining_expected_cents)],
            ]}
          />
          {c.incident_id ? (
            <p className="small">
              Incident lié : <Link to={org.to(`incidents/${c.incident_id}`)}>voir l’incident</Link> ({c.incident_status})
            </p>
          ) : null}
          <p>{c.description}</p>
        </Card>
        {canOp ? (
          <Card title="Mettre à jour">
            <form
              className="stack-sm"
              onSubmit={async (e) => {
                e.preventDefault();
                await act.run(
                  () =>
                    patch(path, {
                      status: status.status || undefined,
                      next_action_on: status.next_action_on || undefined,
                      next_action: status.next_action || undefined,
                      franchise_cents: status.franchise_cents ?? undefined,
                      expected_cents: status.expected_cents ?? undefined,
                    }),
                  "Dossier mis à jour.",
                );
                refresh();
              }}
            >
              <Select label="Statut" value={status.status} onChange={(e) => setStatus({ ...status, status: e.target.value })} options={[{ value: "", label: "Inchangé" }, ...options(CLAIM_STATUS)]} />
              <div className="form-row">
                <Input label="Prochaine action le" type="date" value={status.next_action_on} onChange={(e) => setStatus({ ...status, next_action_on: e.target.value })} />
                <Input label="Prochaine action" value={status.next_action} onChange={(e) => setStatus({ ...status, next_action: e.target.value })} />
              </div>
              <div className="form-row">
                <MoneyInput label="Franchise" cents={status.franchise_cents} onChange={(v) => setStatus({ ...status, franchise_cents: v })} />
                <MoneyInput label="Indemnité attendue" cents={status.expected_cents} onChange={(v) => setStatus({ ...status, expected_cents: v })} />
              </div>
              <Button type="submit" busy={act.busy}>
                Enregistrer
              </Button>
            </form>
          </Card>
        ) : null}
      </div>
      <Card title="Interlocuteurs">
        {contacts.length ? (
          <ul className="list-plain">
            {contacts.map((k: any) => (
              <li key={k.id}>
                <strong>{CLAIM_ROLES[k.role]}</strong> : {k.name}
                {k.reference ? ` · réf. ${k.reference}` : ""}
                {k.contact ? ` · ${k.contact}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Aucun interlocuteur.</p>
        )}
        {canOp ? (
          <form
            className="form-row"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await act.run(() => post(`${path}/contacts`, contact), "Interlocuteur ajouté.")) {
                setContact({ ...contact, name: "", reference: "", contact: "" });
                refresh();
              }
            }}
          >
            <Select label="Rôle" value={contact.role} options={options(CLAIM_ROLES)} onChange={(e) => setContact({ ...contact, role: e.target.value })} />
            <Input label="Nom" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
            <Input label="Référence" value={contact.reference} onChange={(e) => setContact({ ...contact, reference: e.target.value })} />
            <Input label="Contact" value={contact.contact} onChange={(e) => setContact({ ...contact, contact: e.target.value })} />
            <Button type="submit" variant="secondaire">
              Ajouter
            </Button>
          </form>
        ) : null}
      </Card>
      <Card title="Chronologie">
        <Timeline
          items={events.map((e: any) => ({
            id: e.id,
            at: e.event_on,
            who: e.author_name,
            text: `${CLAIM_EVENTS[e.kind] ?? "Pièce"}${e.amount_cents != null ? ` : ${money(e.amount_cents)}` : ""}${e.body ? ` — ${e.body}` : ""}`,
          }))}
        />
        <DocLinks docs={documents} />
        {canOp ? (
          <>
            <form
              className="stack-sm"
              onSubmit={async (e) => {
                e.preventDefault();
                if (await act.run(() => post(`${path}/events`, { ...event, amount_cents: event.amount_cents ?? undefined }), "Étape ajoutée.")) {
                  setEvent({ ...event, amount_cents: null, body: "" });
                  refresh();
                }
              }}
            >
              <h3>Ajouter une étape</h3>
              <div className="form-row">
                <Select label="Étape" value={event.kind} options={options(CLAIM_EVENTS)} onChange={(e) => setEvent({ ...event, kind: e.target.value })} />
                <Input label="Date" type="date" value={event.event_on} onChange={(e) => setEvent({ ...event, event_on: e.target.value })} />
                <MoneyInput label="Montant" cents={event.amount_cents} error={fieldError(act.error, "amount_cents")} onChange={(v) => setEvent({ ...event, amount_cents: v })} />
              </div>
              <Input label="Précision" value={event.body} onChange={(e) => setEvent({ ...event, body: e.target.value })} />
              <div>
                <Button type="submit" variant="secondaire" busy={act.busy}>
                  Ajouter
                </Button>
              </div>
            </form>
            <FileDrop
              label="Ajouter une pièce au dossier"
              help="Réservée à votre espace : les pièces du sinistre ne sont pas partagées avec le locataire."
              onFiles={async (files) => {
                for (const f of files) await act.run(() => sendFile(`${path}/documents`, f), "Pièce ajoutée.");
                refresh();
              }}
            />
          </>
        ) : null}
      </Card>
    </div>
  );
}
